/** Site collections managed in the CMS (customer logos, modules, hardware, glossary, FAQs). */
export type CollectionName = "customer_logos" | "modules" | "hardware" | "glossary" | "home_faqs" | "objection_faqs" | "module_details";

export type FieldType = "text" | "textarea" | "image" | "list" | "json" | "select";
export type FieldDef = { key: string; label: string; type: FieldType; required?: boolean; options?: string[] };
export type CollectionDef = { name: CollectionName; label: string; titleField: string; keyField: string; fields: FieldDef[]; help?: string; translatable?: boolean };

export type CollectionRow = { item_key: string; data: Record<string, unknown>; sort_order: number; is_published: boolean };
export type CollectionState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; seeded: boolean; rows: CollectionRow[] };

/**
 * Which items the public site shows. Bundled fallback only while loading, on a failed load,
 * or before the collection was ever imported. Once imported, the database is authoritative —
 * an empty or fully unpublished collection renders empty instead of resurrecting fallback content.
 */
export function resolveCollection<T>(state: CollectionState, fallback: T[]): T[] {
  if (state.status !== "success" || !state.seeded) return fallback;
  return state.rows
    .filter((r) => r.is_published)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((r) => r.data as T);
}

/** Validates an edited item before saving. Returns an error message or null. */
export function validateItem(def: CollectionDef, key: string, data: Record<string, unknown>): string | null {
  if (!/^[a-z0-9][a-z0-9-]{0,99}$/.test(key)) return "Key must use lowercase letters, numbers and hyphens.";
  for (const f of def.fields) {
    const v = data[f.key];
    if (f.required && (v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length)))
      return `${f.label} is required.`;
    if (f.type === "image" && typeof v === "string" && v && !/^(https:\/\/|\/[^/])/.test(v)) return `${f.label} must be an https URL or a site path.`;
    if (f.type === "select" && f.options && typeof v === "string" && v && !f.options.includes(v)) return `${f.label} has an unknown value.`;
  }
  return null;
}

export const COLLECTIONS: Record<CollectionName, CollectionDef> = {
  customer_logos: {
    name: "customer_logos", label: "Customer Logos", titleField: "name", keyField: "key",
    help: "Homepage customer logo carousel. Customers only — partners are managed separately.",
    fields: [
      { key: "name", label: "Customer name", type: "text", required: true },
      { key: "src", label: "Logo image", type: "image", required: true },
      { key: "url", label: "Website (optional)", type: "text" },
    ],
  },
  modules: {
    name: "modules", label: "Modules", titleField: "title", keyField: "slug",
    help: "Module cards, menu, footer and module page headers. The key is the page URL (/modules/<key>); keep it stable.",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "short", label: "Short line", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "features", label: "Features (one per line)", type: "list" },
      { key: "businessResult", label: "Business result", type: "textarea" },
      { key: "lean", label: "Lean angle", type: "textarea" },
    ],
  },
  hardware: {
    name: "hardware", label: "Hardware", titleField: "name", keyField: "slug",
    help: "Hardware catalog and product pages. The key is the page URL (/hardware/<key>); keep it stable.",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "family", label: "Family", type: "text", required: true },
      { key: "category", label: "Category", type: "select", required: true, options: ["Edge & Connectivity", "Operator Terminals", "Scanning", "RFID", "Printing & Labeling", "Energy Monitoring", "Industrial Displays"] },
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "shortDescription", label: "Short description", type: "textarea", required: true },
      { key: "positioning", label: "Positioning", type: "textarea" },
      { key: "fullDescription", label: "Full description", type: "textarea", required: true },
      { key: "heroImage", label: "Main image", type: "image", required: true },
      { key: "imageAlt", label: "Main image alt text", type: "text" },
      { key: "keyBenefits", label: "Key benefits (one per line)", type: "list" },
      { key: "connectivity", label: "Connectivity (one per line)", type: "list" },
      { key: "useCases", label: "Use cases (one per line)", type: "list" },
      { key: "workflows", label: "Workflows (one per line)", type: "list" },
      { key: "industries", label: "Industries (one per line)", type: "list" },
      { key: "relatedProductSlugs", label: "Related product keys (one per line)", type: "list" },
      { key: "deploymentNotes", label: "Deployment notes", type: "textarea" },
      { key: "ctaLabel", label: "Button label", type: "text" },
      { key: "gallery", label: "Gallery [{src, caption, alt}]", type: "json" },
      { key: "capabilities", label: "Capabilities [{title, desc}]", type: "json" },
      { key: "specifications", label: "Specifications [{label, value}]", type: "json" },
      { key: "compatibleModules", label: "Compatible modules [{name, href}]", type: "json" },
      { key: "sectionHeadings", label: "Section headings {purpose, capabilities, ...}", type: "json" },
    ],
  },
  glossary: {
    name: "glossary", label: "Glossary", titleField: "term", keyField: "slug",
    help: "Glossary index and term pages. The key is the page URL (/glossary/<key>); keep it stable.",
    fields: [
      { key: "term", label: "Term", type: "text", required: true },
      { key: "fullName", label: "Full name", type: "text" },
      { key: "acronym", label: "Acronym", type: "text" },
      { key: "category", label: "Category", type: "select", required: true, options: ["Production Performance", "Manufacturing Systems", "Industrial IoT & Automation", "Energy Management", "Quality Management", "Maintenance & Reliability", "Planning & Traceability", "Lean Manufacturing", "Industrial Hardware"] },
      { key: "shortDefinition", label: "Short definition", type: "textarea", required: true },
      { key: "definition", label: "Definition", type: "textarea", required: true },
      { key: "formula", label: "Formula", type: "text" },
      { key: "whyItMatters", label: "Why it matters", type: "textarea" },
      { key: "relatedTerms", label: "Related term keys (one per line)", type: "list" },
      { key: "searchKeywords", label: "Search keywords (one per line)", type: "list" },
      { key: "aliases", label: "Aliases / redirects (one per line)", type: "list" },
    ],
  },
  home_faqs: {
    name: "home_faqs", label: "Homepage FAQ", titleField: "q", keyField: "key",
    help: "Homepage FAQ and its search-engine FAQ data.",
    fields: [
      { key: "q", label: "Question", type: "text", required: true },
      { key: "a", label: "Answer", type: "textarea", required: true },
    ],
  },
  objection_faqs: {
    name: "objection_faqs", label: "Objections FAQ", titleField: "q", keyField: "key",
    help: "Common-objections answers block.",
    fields: [
      { key: "q", label: "Objection", type: "text", required: true },
      { key: "a", label: "Answer", type: "textarea", required: true },
    ],
  },
  module_details: {
    name: "module_details", label: "Module Pages", titleField: "h1", keyField: "slug", translatable: true,
    help: "Detailed sections of each module page (/modules/<key>). The key must match a module key. Layout, images and URLs stay as they are; unchanged text keeps its existing translations, edited text needs 'Translate'.",
    fields: [
      { key: "category", label: "Eyebrow", type: "text", required: true },
      { key: "h1", label: "Page heading", type: "text", required: true },
      { key: "intro", label: "Intro", type: "textarea", required: true },
      { key: "heroBullets", label: "Hero bullets (one per line)", type: "list" },
      { key: "problems", label: "Problems we solve [{title, desc}]", type: "json" },
      { key: "capabilities", label: "Core capabilities [{title, desc}]", type: "json" },
      { key: "workflow", label: "How it works [{n, title, body}]", type: "json" },
      { key: "roles", label: "Role-based value [{role, pain, value}]", type: "json" },
      { key: "useCases", label: "Manufacturing scenarios [{persona, scenario, outcome}]", type: "json" },
      { key: "dataIn", label: "Data in (one per line)", type: "list" },
      { key: "dataOut", label: "Data out (one per line)", type: "list" },
      { key: "processing", label: "Processing (one per line)", type: "list" },
      { key: "integrations", label: "Integrations & hardware (one per line)", type: "list" },
      { key: "implementation", label: "Implementation steps (one per line)", type: "list" },
      { key: "faq", label: "FAQ [{q, a}]", type: "json" },
      { key: "related", label: "Related module keys (one per line)", type: "list" },
      { key: "ctaHeading", label: "Closing heading", type: "text" },
      { key: "ctaBody", label: "Closing text", type: "textarea" },
    ],
  },
};

export const slugKey = (s: string) =>
  s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 100) || "item";

/** Stable translation key for one English source string (edited text gets a new key, so stale translations are never shown). */
export function textKey(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `t${(h >>> 0).toString(36)}${s.length.toString(36)}`;
}

/** Every non-empty string inside a value (deduplicated), for translation. */
export function collectStrings(v: unknown, out = new Set<string>()): Set<string> {
  if (typeof v === "string") { if (v.trim()) out.add(v); }
  else if (Array.isArray(v)) v.forEach((x) => collectStrings(x, out));
  else if (v && typeof v === "object") Object.values(v).forEach((x) => collectStrings(x, out));
  return out;
}

/** Replace strings that have a stored translation; keys/structure/non-text values are untouched. */
export function applyTextTranslations<T>(v: T, tr: Record<string, unknown> | undefined, skip: ReadonlySet<string> = new Set()): T {
  if (!tr) return v;
  const walk = (x: unknown, k?: string): unknown => {
    if (typeof x === "string") { const t = tr[textKey(x)]; return k && skip.has(k) ? x : typeof t === "string" && t.trim() ? t : x; }
    if (Array.isArray(x)) return x.map((y) => walk(y, k));
    if (x && typeof x === "object") return Object.fromEntries(Object.entries(x).map(([kk, y]) => [kk, walk(y, kk)]));
    return x;
  };
  return walk(v) as T;
}
